import z from "zod";

const translationPath = "page.4uBus.integrationRule.integrationRuleMov.errors.";

const uBusIntegrationRuleMovIProcedClassesSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
  },
  { invalid_type_error: translationPath + "integration_direction_type" },
);

const uBusIntegrationRuleMovProcedTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
  },
  { invalid_type_error: translationPath + "i_service" },
);

const externalIdSelectorSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .nullish();

const externalListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const integrationRuleMovSchema = z.object({
  id: z.string().optional(),
  enable: z.boolean(),
  individualExternalId: z.string().nullish(),
  establishmentExternalId: externalIdSelectorSchema,
  providerExternalId: externalIdSelectorSchema,
  establishmentExternalIdThen: externalIdSelectorSchema,
  patientEncounterTypeExternalIdThen: externalIdSelectorSchema,
  patientEncounterOriginExternalIdThen: externalIdSelectorSchema,
  patientCareCharacterExternalIdThen: externalIdSelectorSchema,
  patientCareClassificationExternalIdThen: externalIdSelectorSchema,
  insuranceExternalIdThen: externalIdSelectorSchema,
  insuranceCategoryExternalIdThen: externalIdSelectorSchema,
  sectorExternalIdThen: externalIdSelectorSchema,
  sectorBasicUnitExternalIdThen: z.string().nullish(),
  sectorComplUnitExternalIdThen: z.string().nullish(),
  sectorAccommodationTypeExternalIdThen: externalIdSelectorSchema,
  tissAttendanceTypeExternalIdThen: externalIdSelectorSchema,
  insuranceTypeExternalIdThen: externalIdSelectorSchema,
  insurancePlanExternalIdThen: externalIdSelectorSchema,
  uBusIntegrationRuleMovIProcedClasses: z
    .array(uBusIntegrationRuleMovIProcedClassesSchema)
    .nullish(),
  uBusIntegrationRuleMovProcedTypes: z
    .array(uBusIntegrationRuleMovProcedTypeSchema)
    .nullish(),
  // Nullish no schema base: o campo (SwitchInput) so e renderizado quando
  // isHvsUpaPs e true (ver IntegrationRuleMov.tsx). Para qualquer outra
  // automacao (19/Itumbiara incluida) ele nunca e montado no form, entao o
  // valor fica undefined - exigir boolean() aqui bloquearia salvar/criar
  // Movimento de QUALQUER automacao que nao seja a HVS. O fallback pra
  // `false` ao montar o payload (ver saveHandler/updateHandler) cobre o
  // contrato com o backend, que espera um bool nao-nulo.
  verifyReleasedOpinion: z.boolean().nullish(),
  // Nullish pelo mesmo motivo de verifyReleasedOpinion acima - so existe pra
  // isHvsUpaPs. Fallback pra "SCHEDULE" no saveHandler/updateHandler.
  doctorObtainMode: z.enum(["SCHEDULE", "LAST_ENCOUNTER_OF_DAY"]).nullish(),
  defaultDoctorExternalIdThen: externalIdSelectorSchema,
  uBusIntegrationRuleMovInsurances: z.array(externalListItemSchema).nullish(),
  uBusIntegrationRuleMovSectors: z.array(externalListItemSchema).nullish(),
  uBusIntegrationRuleMovProcedureAreas: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovProcedureSpecialties: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovProcedureGroups: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovLabExamClassifications: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovInternalProcedures: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovInternalProceduresThen: z
    .array(externalListItemSchema)
    .nullish(),
  uBusIntegrationRuleMovSchedulesThen: z
    .array(externalListItemSchema)
    .nullish(),
});

export type TIntegrationRuleMovForm = z.infer<typeof integrationRuleMovSchema>;

// Campos exigidos por Mauricio (2026-08-11) apenas quando o formulario e da
// automacao Tasy OPS (#2936): os 2 campos do "Se" e todos os do "Entao"
// exceto sectorComplUnitExternalIdThen (unico que ele marcou como nao
// obrigatorio).
const REQUIRED_WHEN_TASY_OPS_SELECTOR_FIELDS = [
  "establishmentExternalId",
  "providerExternalId",
  "establishmentExternalIdThen",
  "patientEncounterTypeExternalIdThen",
  "patientEncounterOriginExternalIdThen",
  "patientCareCharacterExternalIdThen",
  "patientCareClassificationExternalIdThen",
  "insuranceExternalIdThen",
  "insuranceCategoryExternalIdThen",
  "sectorExternalIdThen",
  "sectorAccommodationTypeExternalIdThen",
] as const;

// Campos exigidos para o fluxo HVS UPA->PS (automacao #3087), conforme o
// escopo original: Setor/Unidade Basica/Tipo TISS do "Entao" sao obrigatorios.
// Unidade Complementar e opcional (como na Itumbiara): no Tasy a maioria dos
// setores so tem unidades com complemento em branco, e o Robots ja trata o
// vazio como " ". Exigir valor aqui forca digitar uma combinacao inexistente
// em unidade_atendimento (ATEPACU_UNIATEN_FK na transferencia). Medico padrao
// tambem e opcional.
const REQUIRED_WHEN_HVS_UPA_PS_SELECTOR_FIELDS = [
  "sectorExternalIdThen",
  "tissAttendanceTypeExternalIdThen",
] as const;

const REQUIRED_WHEN_HVS_UPA_PS_LIST_FIELDS = [
  "uBusIntegrationRuleMovInternalProceduresThen",
] as const;

export const getIntegrationRuleMovSchema = (
  isTasyOps: boolean,
  isHvsUpaPs: boolean,
) =>
  integrationRuleMovSchema.superRefine((data, ctx) => {
    if (isTasyOps) {
      REQUIRED_WHEN_TASY_OPS_SELECTOR_FIELDS.forEach((field) => {
        if (!data[field]?.id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: translationPath + toErrorKey(field),
          });
        }
      });

      if (!data.sectorBasicUnitExternalIdThen?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sectorBasicUnitExternalIdThen"],
          message: translationPath + "sector_basic_unit_external_id_then",
        });
      }
    }

    if (isHvsUpaPs) {
      REQUIRED_WHEN_HVS_UPA_PS_SELECTOR_FIELDS.forEach((field) => {
        if (!data[field]?.id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: translationPath + toErrorKey(field),
          });
        }
      });

      if (!data.sectorBasicUnitExternalIdThen?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sectorBasicUnitExternalIdThen"],
          message: translationPath + "sector_basic_unit_external_id_then",
        });
      }

      REQUIRED_WHEN_HVS_UPA_PS_LIST_FIELDS.forEach((field) => {
        if (!data[field] || data[field]!.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: translationPath + toErrorKey(field),
          });
        }
      });

      // Escala so e obrigatoria no modo SCHEDULE (padrao/registros antigos sem
      // doctorObtainMode ainda salvo). No modo LAST_ENCOUNTER_OF_DAY o campo
      // fica oculto no form (ver ScheduleThen em IntegrationRuleMov.tsx) e nao
      // faz sentido exigir.
      if (
        data.doctorObtainMode !== "LAST_ENCOUNTER_OF_DAY" &&
        (!data.uBusIntegrationRuleMovSchedulesThen ||
          data.uBusIntegrationRuleMovSchedulesThen.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["uBusIntegrationRuleMovSchedulesThen"],
          message:
            translationPath + toErrorKey("uBusIntegrationRuleMovSchedulesThen"),
        });
      }
    }
  });

function toErrorKey(field: string) {
  return field.replace(/[A-Z]/g, (letter) => "_" + letter.toLowerCase());
}
